import { DetailsIssuedAlert } from '@/entities';
import { selectEmployeesInitials } from '@/entities/employee';
import { MKSelectPreform } from '@/features/mk-select/mk-select-preform';
import { CalendarSinglePicker, CustomSelect, CustomInput } from '@/shared';
import {
	DEFAULT_DATE_FORMAT,
	DEFAULT_DATE_TIME_FORMAT_WITH_TZ,
	RETURN_COUNT_ERROR,
	NUM_REMAINDER_TOO_LOW,
	SGD_OUT_COUNT_TOO_LARGE,
	CANT_CHANGE_SGD_OUT_TECH_MAP_ERROR,
	PRODUCT_TECH_MAP_CANCELLED,
} from '@/shared/constants/constants';
import styles from '@/shared/components/custom-select/custom-select.module.scss';
import {
	BodyUpdatePreformTechMap,
	PreformCustomNumResponse,
	PreformSgdOutCreateRequest,
	useUpdatePreformTechMapMutation,
} from '@/shared/state/api/swagger';
import { useOperationNotification } from '@/widgets/operation/hooks';
import { Button, chakra } from '@chakra-ui-kraud/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { IssueDetailsFormPreformProps } from '../../../model/operation.types';
import { issueDetailsPreformSchema } from '../../../model/operations.schema';
import { OperationLayout } from '../../operation-layout/operation-layout';
import { OperationFormHeader } from '../form-header/form-header';
import { useGetAllInitialsQuery } from '@/shared/state/api/swagger';

dayjs.extend(utc);
dayjs.extend(timezone);

type IssueDetailsFormValues = Omit<PreformSgdOutCreateRequest, 'fio'> & {
	fio: string | number;
	product_tech_map_receiver_number: number;
	preform_num: PreformCustomNumResponse;
};

export const IssueDetailsFormPreform = ({ techMap, mappedEmployees }: IssueDetailsFormPreformProps) => {
	const [groupVisibility, setGroupVisibility] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const { showErrorToast } = useOperationNotification();
	const [updatePreformTechMap, { isSuccess }] = useUpdatePreformTechMapMutation();

	const { options: employees } = useGetAllInitialsQuery(
		{ stringToSearch: '' },
		{
			selectFromResult: (result) => ({ ...result, options: selectEmployeesInitials(result?.data) }),
		},
	);

	const form = useForm<IssueDetailsFormValues>({
		resolver: yupResolver(issueDetailsPreformSchema),
		mode: 'all',
		defaultValues: {
			date: dayjs().format(DEFAULT_DATE_FORMAT),
			return_date: dayjs().format(DEFAULT_DATE_FORMAT),
			count: '',
			return_count: '',
			preform_num: techMap?.preform_num,
		},
	});

	const { control, handleSubmit, setValue, trigger } = form;

	const onSubmit = (data: IssueDetailsFormValues) => {
		setIsFetching(true);

		const fio_receiver = mappedEmployees.find(
			(employee) => Number(employee.value.split('_')[0]) === Number(data.fio_receiver),
		)?.label;
		const fio_sender = mappedEmployees.find(
			(employee) => Number(employee.value.split('_')[0]) === Number(data.fio_sender),
		)?.label;
		const fio_receiver_returned = mappedEmployees.find(
			(employee) => Number(employee.value.split('_')[0]) === Number(data.fio_receiver_returned),
		)?.label;

		const preformSgdOutToCreate: PreformSgdOutCreateRequest[] = [
			{
				...data,
				date: dayjs(dayjs(data.date).format('YYYY-MM-DD') + 'T' + dayjs().format('HH:mm:ss')).format(
					DEFAULT_DATE_TIME_FORMAT_WITH_TZ,
				),
				return_date: dayjs(
					dayjs(data.return_date).format('YYYY-MM-DD') + 'T' + dayjs().format('HH:mm:ss'),
				).format(DEFAULT_DATE_TIME_FORMAT_WITH_TZ),
				fio_receiver,
				fio_sender,
				fio_receiver_returned,
			},
		];

		const payload: BodyUpdatePreformTechMap = {
			preform_sgd_out_payload: {
				preform_sgd_out_to_create: preformSgdOutToCreate,
				preform_sgd_out_to_update: techMap.preform_sgd_out,
			},
		};

		updatePreformTechMap({
			preformTechMapId: techMap.id,
			bodyUpdatePreformTechMap: payload,
		})
			.unwrap()
			.catch((err) => {
				if (err?.status) {
					switch (err?.status) {
						case PRODUCT_TECH_MAP_CANCELLED: {
							showErrorToast('Не удалось сохранить карту', 'Выбранная МК на деталь была отменена');
							break;
						}
						case NUM_REMAINDER_TOO_LOW: {
							showErrorToast(
								'Не удалось выдать материал',
								`На складе осталось ${err?.data?.detail?.remainder} материала`,
							);
							break;
						}
						case RETURN_COUNT_ERROR: {
							showErrorToast(
								'Не удалось сохранить карту',
								`Вы не можете вернуть материала больше чем ${err?.data?.detail?.available_return_count}`,
							);
							break;
						}
						case SGD_OUT_COUNT_TOO_LARGE: {
							showErrorToast(
								'Не удалось выдать материал',
								`Количество оставшегося материала: ${techMap?.remaining_count}`,
							);
							break;
						}
						case CANT_CHANGE_SGD_OUT_TECH_MAP_ERROR: {
							showErrorToast('Не удалось сохранить карту', 'В выбранной ДМК техпроцесс уже начат');
							break;
						}
						default: {
							showErrorToast(
								'Не удалось сохранить карту',
								'При сохранении заготовительной МК произошла ошибка',
							);
						}
					}
				}
			})
			.finally(() => setIsFetching(false));
	};

	if (isSuccess) return <DetailsIssuedAlert productTechMapNumber={techMap?.number} />;

	return (
		<OperationLayout
			header={<OperationFormHeader techMap={techMap} />}
			footer={
				<Button width="100%" onClick={handleSubmit(onSubmit)} isDisabled={isFetching}>
					Сохранить
				</Button>
			}
		>
			<FormProvider {...form}>
				<chakra.div display="flex" flexDirection="column" gap="20px">
					<MKSelectPreform
						bordered={true}
						name={`product_tech_map_receiver_number`}
						style={{
							borderRadius: '6px',
							border: '1px solid #e2e8f0',
							paddingBlock: '4px',
						}}
						onSelect={(item) => {
							setValue(`product_tech_map_receiver_id`, item.id!);
							setValue(`product_tech_map_receiver_number`, item.number!);
							trigger();
						}}
					/>
					<Controller
						name="date"
						control={control}
						render={({ field: { onChange } }) => (
							<CalendarSinglePicker
								maxDate={new Date(2050, 1)}
								clearable={true}
								defaultDate={dayjs().format(DEFAULT_DATE_FORMAT)}
								label="Дата выдачи"
								onSelectDate={(date) => onChange(date)}
							/>
						)}
					/>
					<Controller
						name={`count`}
						control={control}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<CustomInput
								onChange={onChange}
								isInvalid={!!error}
								value={value ?? ''}
								name={`count`}
								label="Кол-во выданного м X шт.	"
							/>
						)}
					/>
					<Controller
						name={`fio_receiver`}
						control={control}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<CustomSelect
								onFocus={() => setGroupVisibility(true)}
								onBlur={() => setGroupVisibility(false)}
								showSearch
								allowClear={false}
								options={employees}
								value={value}
								onChange={onChange}
								filterOption={(input, option) =>
									String(option?.label ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								optionFilterProp="children"
								dropdownStyle={{ zIndex: 1400 }}
								size="large"
								label="Получатель"
								className={clsx(
									styles['select'],
									styles['select-large'],
									groupVisibility && styles['select-focus'],
									error && [styles['select-invalid']],
								)}
							/>
						)}
					/>
					<Controller
						name={`fio_sender`}
						control={control}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<CustomSelect
								onFocus={() => setGroupVisibility(true)}
								onBlur={() => setGroupVisibility(false)}
								showSearch
								allowClear={false}
								options={employees}
								value={value}
								onChange={onChange}
								filterOption={(input, option) =>
									String(option?.label ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								optionFilterProp="children"
								dropdownStyle={{ zIndex: 1400 }}
								size="large"
								label="ФИО выдавшего материал"
								className={clsx(
									styles['select'],
									styles['select-large'],
									groupVisibility && styles['select-focus'],
									error && [styles['select-invalid']],
								)}
							/>
						)}
					/>
					<Controller
						name="return_date"
						control={control}
						render={({ field: { onChange } }) => (
							<CalendarSinglePicker
								maxDate={new Date(2050, 1)}
								clearable={true}
								defaultDate={dayjs().format(DEFAULT_DATE_FORMAT)}
								label="Дата возврата"
								onSelectDate={(date) => onChange(date)}
							/>
						)}
					/>
					<Controller
						name={`return_count`}
						control={control}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<CustomInput
								onChange={onChange}
								isInvalid={!!error}
								value={value ?? ''}
								name={`return_count`}
								label="Кол-во вернувшегося м X шт."
							/>
						)}
					/>
					<Controller
						name={`fio_receiver_returned`}
						control={control}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<CustomSelect
								onFocus={() => setGroupVisibility(true)}
								onBlur={() => setGroupVisibility(false)}
								showSearch
								allowClear={false}
								options={employees}
								value={value}
								onChange={onChange}
								filterOption={(input, option) =>
									String(option?.label ?? '')
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								optionFilterProp="children"
								dropdownStyle={{ zIndex: 1400 }}
								size="large"
								label="ФИО принявшего материал"
								className={clsx(
									styles['select'],
									styles['select-large'],
									groupVisibility && styles['select-focus'],
									error && [styles['select-invalid']],
								)}
							/>
						)}
					/>
				</chakra.div>
			</FormProvider>
		</OperationLayout>
	);
};
